import React, { useState, useRef } from "react";
import loginIcons from "../assets/signin.gif";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { CheckCircle, XCircle } from "lucide-react";
import SummaryApi from "../common";

const AdminLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [data, setData] = useState({
    user_name: "", // Key updated to match payload
    password: "",
  });

  const ADMIN_TOAST_ID = "admin-login-toast";
  const toastLock = useRef(false);
  const navigate = useNavigate();

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const showSuccessToast = (msg) => {
    if (toastLock.current) return;
    toastLock.current = true;

    toast.custom(
      () => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-rose-500"
        >
          <div className="flex-1 p-4">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-500">
                <CheckCircle size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-rose-950">Login Successful!</p>
                <p className="text-xs text-rose-900/60 mt-1">
                  {msg || "Welcome back to EVEYA Admin Portal."}
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss(ADMIN_TOAST_ID)}
            className="px-4 font-bold text-rose-600"
          >
            Close
          </button>
        </motion.div>
      ),
      {
        id: ADMIN_TOAST_ID,
        duration: 1000,
        onClose: () => {
          toastLock.current = false;
        },
      }
    );
  };

  const showErrorToast = (msg) => {
    if (toastLock.current) return;
    toastLock.current = true;

    toast.custom(
      () => (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-auto max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] flex border-b-4 border-red-500"
        >
          <div className="flex-1 p-4">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center text-red-500">
                <XCircle size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-red-950">Login Failed</p>
                <p className="text-xs text-red-900/60 mt-1">
                  {msg || "Invalid username or password."}
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => toast.dismiss(ADMIN_TOAST_ID)}
            className="px-4 font-bold text-red-600"
          >
            Close
          </button>
        </motion.div>
      ),
      {
        id: ADMIN_TOAST_ID,
        duration: 1000,
        onClose: () => {
          toastLock.current = false;
        },
      }
    );
  };

 const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(SummaryApi.adminLogin.url, {
        method: SummaryApi.adminLogin.method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_name: data.user_name,
          password: data.password,
        }),
      });

      const dataResponse = await response.json();

      // FIXED: Changed .success to .status based on your API response
      if (dataResponse.status) { 
        showSuccessToast(dataResponse.message);

        // FIXED: Using admin_id from your specific API response
        const adminId = dataResponse?.data?.admin_id || "12345";
        sessionStorage.setItem("H7Z4L9VD2XK5JW8QRYT6", btoa(adminId.toString()));

        setTimeout(() => {
          navigate("/admin-panel/dashboard");
        }, 1600);
      } else {
        showErrorToast(dataResponse.message);
      }
    } catch (error) {
      console.error("Login error", error);
      showErrorToast("Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <section
        id="login"
        className="min-h-screen bg-gradient-to-b from-pink-50 via-pink-100 to-pink-200 flex items-center justify-center py-8 px-4"
      >
        <div className="w-full max-w-lg mx-auto">
          <div className="bg-white shadow-xl rounded-2xl p-6 sm:p-8 border border-pink-100">
            <div className="text-center mb-6">
              <h1 className="text-4xl font-extrabold text-pink-700 tracking-wide">
                EVEYA
              </h1>
              <p className="text-pink-600 text-base font-medium mt-2">
                Admin Portal
              </p>
              <div className="w-24 h-px bg-gradient-to-r from-transparent via-pink-300 to-transparent mx-auto mt-3"></div>
            </div>

            <div className="w-24 h-24 mx-auto mb-6 rounded-full overflow-hidden border-6 border-pink-100 shadow-lg">
              <img
                src={loginIcons}
                alt="Login illustration"
                className="w-full h-full object-cover"
              />
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label className="block text-pink-800 font-semibold text-base mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  placeholder="Enter username"
                  name="user_name"
                  value={data.user_name}
                  onChange={handleOnChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-pink-200 focus:border-pink-500 focus:outline-none transition-all bg-pink-50/50 placeholder-pink-400 text-gray-800"
                  required
                />
              </div>

              <div>
                <label className="block text-pink-800 font-semibold text-base mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    name="password"
                    value={data.password}
                    onChange={handleOnChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-pink-200 focus:border-pink-500 focus:outline-none transition-all bg-pink-50/50 placeholder-pink-400 text-gray-800 pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-pink-600 hover:text-pink-700 text-xl transition"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <Link
                  to="/forgot-password"
                  className="block text-right mt-2 text-pink-600 hover:text-pink-700 font-medium text-sm underline-offset-2 hover:underline transition"
                >
                  Forgot password?
                </Link>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white font-bold py-4 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  LOGIN
                </button>
              </div>
            </form>

            <div className="text-center mt-8">
              <p className="text-pink-700 font-medium text-sm">
                Welcome back to <span className="font-bold">EVEYA</span> Admin
              </p>
              <p className="text-pink-500 text-xs mt-2">© 2026 EVEYA Beauty</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AdminLogin;